import IMask from "imask";
import Cookie from "js-cookie";
import { HttpError } from "../../types/HttpError.js";
import z, { ZodError } from "zod";
import { LEGACY_COOKIE_WC_BD_ZIP, LEGACY_COOKIE_WC_BD_WHS_DEFAULT_VALUE, LEGACY_COOKIE_WC_BD_WHS } from "../../components/Chrome/BrowseContext/constants.js";
import { US_FORMAT_CONFIGURATION } from "./lib.js";
const CAValidationSchema = z.object({
  zipCode: z.string().regex(
    /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][ -]?[0-9][ABCEGHJ-NPRSTV-Z][0-9]$/i
  )
  // REGEX from https://stackoverflow.com/a/43903635
});
const CANADA_FORMAT_CONFIGURATION = {
  inputMaskConfiguration: {
    patterns: ["a0a 0a0"],
    prepare: (str) => str.toUpperCase()
  },
  maxCharacterLimit: 7,
  validationSchema: CAValidationSchema
};
const DELIVERY_LOCATION_LOCALE_VALIDATION_CONFIGURATION = {
  "en-ca": CANADA_FORMAT_CONFIGURATION,
  "en-us": US_FORMAT_CONFIGURATION,
  "fr-ca": CANADA_FORMAT_CONFIGURATION
};
const createIMask = (inputMaskConfiguration) => IMask.createMask({
  mask: inputMaskConfiguration.patterns.map((pattern) => ({
    mask: pattern,
    ...inputMaskConfiguration.prepare && {
      prepare: inputMaskConfiguration.prepare
    }
  }))
});
const parseResponseForErrors = (response, deliveryDateCookie) => {
  if (!response) {
    return {
      errorConfig: {
        severity: "alert",
        type: "serviceDownError"
      }
    };
  }
  let errorConfig;
  const statusMessage = response == null ? void 0 : response.StatusMessage;
  const splashPage = response == null ? void 0 : response.NewWarehouseSplashPageURL;
  const inactiveZipMessage = response == null ? void 0 : response.InactiveZipMessage;
  const externalCarrierChange = response == null ? void 0 : response.ExternalCarrierChange;
  const warehouseChange = response == null ? void 0 : response.WarehouseChange;
  const newWarehouseAlert = response == null ? void 0 : response.NewWarehouseAlert;
  if (response == null ? void 0 : response.errorCode) {
    errorConfig = {
      severity: "alert",
      type: "serviceDownError"
    };
  } else if (inactiveZipMessage) {
    errorConfig = {
      severity: "critical",
      type: "server",
      message: inactiveZipMessage
    };
  } else if (statusMessage === "Warehouse Not Active" && splashPage && window) {
    window.location.href = splashPage;
    return;
  } else if (statusMessage === "Delivery Zip Not Found Segmented Zip Required") {
    errorConfig = {
      type: "enterFullDeliveryAddress",
      severity: "alert"
    };
  } else if (statusMessage === "Delivery Zip Not Found" || statusMessage === "Warehouse Not Active") {
    errorConfig = {
      severity: "critical",
      type: "deliveryNotAvailable"
    };
  } else if (statusMessage === "Delivery Zip Same") {
    errorConfig = {
      type: "deliveryZipSame",
      severity: "general"
    };
  } else ;
  if (errorConfig) {
    return { errorConfig };
  }
  if ((statusMessage === "Delivery Zip Found" || statusMessage === "Delivery Zip Change") && deliveryDateCookie && externalCarrierChange) {
    return {
      confirmationConfig: {
        type: "NDEnabledDrawerBody"
      }
    };
  }
  if (newWarehouseAlert && (externalCarrierChange || warehouseChange)) {
    return {
      confirmationConfig: {
        type: "confirmationTemplate",
        message: newWarehouseAlert
      }
    };
  }
  return {};
};
const validateDeliveryLocation = async (agent, validationSchema, values, bdSettings = {}, locale) => {
  var _a;
  let errors;
  let city;
  let postalCode;
  let state;
  let warehouse;
  let country;
  let isNationalDeliveryEnabledForWareHouse;
  let isStorePickupEnabledForWareHouse;
  let isBDTruckZoneEnabledForWareHouse;
  let showDeliveryMethodPopup = false;
  let errorConfig;
  let confirmationConfig;
  let descriptionToDisplay;
  const { isBDPage, onlyValidate = false } = bdSettings;
  const deliveryDateCookie = Cookie.get("deliveryDateCookie");
  try {
    validationSchema.parse(values);
    if (isBDPage) {
      try {
        if (onlyValidate) {
          const BusinessDeliveryZipValidationAPIResponse = await agent.businessDeliveryZipValidation(
            values.zipCode.trim(),
            Cookie.get(LEGACY_COOKIE_WC_BD_ZIP),
            !!bdSettings.isInitiatedFromChangeAddressType
          );
          postalCode = BusinessDeliveryZipValidationAPIResponse == null ? void 0 : BusinessDeliveryZipValidationAPIResponse.SetDeliveryZip;
          isNationalDeliveryEnabledForWareHouse = (BusinessDeliveryZipValidationAPIResponse == null ? void 0 : BusinessDeliveryZipValidationAPIResponse.isNationalDeliveryEnabledForWareHouse) || false;
          isStorePickupEnabledForWareHouse = (BusinessDeliveryZipValidationAPIResponse == null ? void 0 : BusinessDeliveryZipValidationAPIResponse.isStorePickupEnabledForWareHouse) || false;
          isBDTruckZoneEnabledForWareHouse = (BusinessDeliveryZipValidationAPIResponse == null ? void 0 : BusinessDeliveryZipValidationAPIResponse.isBDTruckZoneEnabledForWareHouse) || false;
          showDeliveryMethodPopup = (BusinessDeliveryZipValidationAPIResponse == null ? void 0 : BusinessDeliveryZipValidationAPIResponse.ShowDeliveryMethodPopup) || false;
          const parsedErrror = parseResponseForErrors(
            BusinessDeliveryZipValidationAPIResponse,
            deliveryDateCookie
          );
          ({ errorConfig, confirmationConfig, descriptionToDisplay } = parsedErrror);
        } else {
          const BusinessDeliveryBrowseAPIResponse = await agent.businessDeliveryBrowse(
            locale,
            values.zipCode.trim(),
            Cookie.get(LEGACY_COOKIE_WC_BD_ZIP) || "",
            bdSettings.userSelectionDeliveryMethod
          );
          postalCode = bdSettings.userSelectionDeliveryMethod === "Store-Pick-Up" ? BusinessDeliveryBrowseAPIResponse == null ? void 0 : BusinessDeliveryBrowseAPIResponse.storePickupZipCode : BusinessDeliveryBrowseAPIResponse == null ? void 0 : BusinessDeliveryBrowseAPIResponse.SetDeliveryZip;
          city = BusinessDeliveryBrowseAPIResponse == null ? void 0 : BusinessDeliveryBrowseAPIResponse.city;
          state = BusinessDeliveryBrowseAPIResponse == null ? void 0 : BusinessDeliveryBrowseAPIResponse.state;
          country = BusinessDeliveryBrowseAPIResponse == null ? void 0 : BusinessDeliveryBrowseAPIResponse.country;
          warehouse = Cookie.get(LEGACY_COOKIE_WC_BD_WHS) || LEGACY_COOKIE_WC_BD_WHS_DEFAULT_VALUE;
          if (BusinessDeliveryBrowseAPIResponse == null ? void 0 : BusinessDeliveryBrowseAPIResponse.errorCode) {
            errorConfig = {
              severity: "alert",
              type: "serviceDownError"
            };
          } else if (BusinessDeliveryBrowseAPIResponse == null ? void 0 : BusinessDeliveryBrowseAPIResponse.errorMessage) {
            errorConfig = {
              severity: "critical",
              type: "server",
              message: BusinessDeliveryBrowseAPIResponse == null ? void 0 : BusinessDeliveryBrowseAPIResponse.errorMessage
            };
          } else if ((BusinessDeliveryBrowseAPIResponse == null ? void 0 : BusinessDeliveryBrowseAPIResponse.ResidentialStatusMessage) === "Delivery Zip Not Found") {
            errorConfig = {
              severity: "critical",
              type: "deliveryNotAvailable"
            };
          }
        }
      } catch (err) {
        if (err instanceof HttpError) {
          const error = err;
          if ((error == null ? void 0 : error.status) === 404) {
            errors = {
              zipCode: "Geo-Location Service failed to find valid ZIP Code"
            };
          } else {
            errorConfig = {
              severity: "alert",
              type: "serviceDownError"
            };
          }
        } else {
          errorConfig = {
            severity: "alert",
            type: "serviceDownError"
          };
        }
      }
    } else {
      try {
        const geoServiceResults = await agent.getGeoServiceLocation(
          values.zipCode
        );
        const firstGeoServiceResult = geoServiceResults == null ? void 0 : geoServiceResults[0];
        city = firstGeoServiceResult == null ? void 0 : firstGeoServiceResult.city;
        postalCode = firstGeoServiceResult == null ? void 0 : firstGeoServiceResult.postalCode;
        state = firstGeoServiceResult == null ? void 0 : firstGeoServiceResult.stateProvinceAbbreviation;
      } catch (err) {
        const errorInstance = err;
        if ((errorInstance == null ? void 0 : errorInstance.cause) instanceof HttpError) {
          if (((_a = errorInstance == null ? void 0 : errorInstance.cause) == null ? void 0 : _a.status) === 404) {
            errors = {
              zipCode: "Geo-Location Service failed to find valid ZIP Code"
            };
          } else {
            errorConfig = {
              severity: "alert",
              type: "serviceDownError"
            };
          }
        } else {
          errorConfig = {
            severity: "alert",
            type: "serviceDownError"
          };
        }
      }
    }
  } catch (error) {
    if (error instanceof ZodError) {
      errors = error.formErrors.fieldErrors;
    }
  }
  return {
    warehouse,
    country,
    city,
    errors,
    postalCode,
    state,
    ...isBDPage && {
      deliveryMethodsConfig: {
        showDeliveryMethodPopup,
        isNationalDeliveryEnabledForWareHouse,
        isStorePickupEnabledForWareHouse,
        isBDTruckZoneEnabledForWareHouse,
        descriptionToDisplay
      }
    },
    ...!!errorConfig && { errorConfig },
    ...!!confirmationConfig && { confirmationConfig }
  };
};
export {
  DELIVERY_LOCATION_LOCALE_VALIDATION_CONFIGURATION,
  createIMask,
  validateDeliveryLocation
};
